import React, { useRef, useImperativeHandle } from "react";
import { mdiMenuUp, mdiMenuDown } from "@mdi/js";
import { Svg } from "./Svg";
import styled from "styled-components";
import commonUtils from "../../script/utils/commonUtils";

const SpinnerStyle = styled.div`
    width: 100%;
`;

const ContainerStyle = styled.div`
    height: 26px;
    display: flex;
    border-radius: 2px;
    box-sizing: border-box;
    background-color: ${props => props.theme.white};
    border: 1px solid ${props => props.theme.greyLight};
    &:focus-within {
        border-color: ${props => props.theme.white};
        outline: 1px solid ${props => props.theme.primaryLight};
    }
    &.disabled {
        user-select: none;
        pointer-events: none;
        border: 1px solid ${props => props.theme.greyLight};
        background-color: ${props => props.theme.greyLighter};
        svg {
            fill: ${props => props.theme.greyDarker};
        }
    }
`;

const TitleStyle = styled.p`
    height: 12px;
    font-size: 12px;
    user-select: none;
    margin-bottom: 1px;
    letter-spacing: 0.4px;
    color: ${props => props.theme.black};
`;

const InputStyle = styled.input`
    width: 100%;
    border: none;
    display: block;
    font-weight: 400;
    box-sizing: border-box;
    padding: 3px 5px 2px 10px;
    transition: border 0.3s, color 0.3s;
    color: ${props => props.theme.black};
    text-align: ${props => props.$textAlign};
    &::placeholder {
        color: ${props => props.theme.greyLight};
    }
    &:disabled {
        background-color: ${props => props.theme.greyLighter};
    }
`;

const ControlStyle = styled.div`
    cursor: pointer;
    border-left: 1px solid ${props => props.theme.greyLight};
    >div {
        height: 12.2px;
        padding: 0 4px;
        user-select: none;
        &:hover {
            background-color: ${props => props.theme.greyLighter};
        }
        &:active {
            background-color: ${props => props.theme.greyDark};
        }
        >svg {
            margin-top: -2px;
        }
    }
`;

export const Spinner = props => {

    const { id, ref, name, title, disabled, textAlign = "left" } = props;

    const min = Number(props.min);
    const max = Number(props.max);
    const value = commonUtils.isBlank(props.value) ? "" : Number(props.value);
    const step = Number(props.step || 1);

    const inputRef = useRef(null);

    const disabledClassName = disabled ? "disabled" : "";

    useImperativeHandle(ref, () => ({
        get value() {
            return inputRef.current.value;
        },
        focus: () => inputRef.current.focus(),
        addEventListener: (type, listener) => inputRef.current.addEventListener(type, listener),
        removeEventListener: (type, listener) => inputRef.current.removeEventListener(type, listener)
    }), [inputRef]);

    const onClickUp = () => {
        const nextValue = value + step;
        onChange(nextValue > max ? min : nextValue);
    };

    const onClickDown = () => {
        const nextValue = value - step;
        onChange(nextValue < min ? max : nextValue);
    };

    //prevents the spinner from losing focus when arrow is clicked
    const onMouseDown = event => event.preventDefault();

    const onKeyDown = event => {
        const { key } = event;
        const allowed = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Home", "End", "Tab"];
        if (key === "ArrowUp") {
            event.preventDefault();
            onClickUp();
        } else if (key === "ArrowDown") {
            event.preventDefault();
            onClickDown();
        } else if (allowed.includes(key)) {
            //allow
        } else if (!/[0-9]/.test(key)) {
            event.preventDefault();
        }
        if (props.onKeyDown) {
            props.onKeyDown(event);
        }
    };

    const onChange = value => {
        inputRef.current.focus();
        const isBlank = commonUtils.isBlank(value);
        if (props.onChange && !disabled) {
            if (isBlank) {
                props.onChange(null);
            } else if (isValid(value)) {
                props.onChange(parseInt(value, 10));
            }
        }
    };

    const isValid = value => {
        const nextValue = parseInt(value, 10);
        const isInteger = Number.isInteger(nextValue);
        const inRange = nextValue >= min && nextValue <= max;
        return isInteger && inRange;
    };

    return (
        <SpinnerStyle>
            {title !== false &&
                <TitleStyle>{title}</TitleStyle>
            }
            <ContainerStyle className={disabledClassName}>
                <InputStyle
                    id={id}
                    min={min}
                    max={max}
                    name={name}
                    value={value}
                    ref={inputRef}
                    disabled={disabled}
                    onKeyDown={onKeyDown}
                    tabIndex={disabled ? -1 : 0}
                    onChange={event => onChange(event.target.value)}
                    $textAlign={textAlign} />
                <ControlStyle>
                    <div onClick={onClickUp} onMouseDown={onMouseDown}>
                        <Svg path={mdiMenuUp} size="18" />
                    </div>
                    <div onClick={onClickDown} onMouseDown={onMouseDown}>
                        <Svg path={mdiMenuDown} size="18" viewBox="0 2 24 24" />
                    </div>
                </ControlStyle>
            </ContainerStyle>
        </SpinnerStyle>
    );
};